import { useEffect, useState } from 'react';
import { Coffee, Search, RefreshCw } from 'lucide-react';
import { api } from './api';
import { errorText, translate } from './i18n';
import { text, type Locale } from '../shared/content';
import type { Character, Shop } from '../shared/types';

export default function ShopDirectory({
  character,
  locale,
  busy,
  onBuy,
}: {
  character: Character;
  locale: Locale;
  busy: boolean;
  onBuy: (shopId: string) => void;
}) {
  const [query, setQuery] = useState(''),
    [search, setSearch] = useState(''),
    [friends, setFriends] = useState(false);
  const [cursors, setCursors] = useState<string[]>([]),
    [shops, setShops] = useState<Shop[]>([]),
    [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true),
    [error, setError] = useState<unknown>(null),
    [retry, setRetry] = useState(0);
  const cursor = cursors.at(-1);
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  useEffect(() => {
    let active = true;
    let fetching = false;
    const load = async () => {
      if (fetching || document.visibilityState === 'hidden') return;
      fetching = true;
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ q: search, friends: friends ? '1' : '0' });
      if (cursor) params.set('cursor', cursor);
      try {
        const result = await api(`/shops?${params}`);
        if (active) {
          setShops(result.shops);
          setNext(result.nextCursor);
        }
      } catch (e) {
        if (active) setError(e);
      } finally {
        fetching = false;
        if (active) setLoading(false);
      }
    };
    setShops([]);
    setNext(null);
    void load();
    const interval = setInterval(() => void load(), 15000);
    const visible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', visible);
    return () => {
      active = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [character.id, character.cityId, character.revision, search, friends, cursor, retry]);
  return (
    <section className="shop-directory">
      <h3>{text('Discover local cafés', '发现街区小店', '發現街區小店', 'Descubre cafeterías')[locale]}</h3>
      <form
        className="directory-search"
        onSubmit={(event) => {
          event.preventDefault();
          setCursors([]);
          setSearch(query.trim());
          setRetry((n) => n + 1);
        }}
      >
        <input
          aria-label={
            text('Café or owner name', '店名或店主名字', '店名或店主名字', 'Cafetería o propietario')[locale]
          }
          placeholder={
            text('Café or owner name', '搜索店名或店主名字', '搜尋店名或店主名字', 'Cafetería o propietario')[
              locale
            ]
          }
          maxLength={48}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <button
          className="button soft compact"
          type="submit"
          aria-label={text('Search cafés', '搜索小店', '搜尋小店', 'Buscar cafeterías')[locale]}
        >
          <Search size={16} />
        </button>
      </form>
      <label className="directory-friends">
        <input
          type="checkbox"
          checked={friends}
          onChange={(event) => {
            setFriends(event.target.checked);
            setCursors([]);
          }}
        />
        {text("Friends' cafés", '只看好友的小店', '只看好友的小店', 'Cafeterías de amigos')[locale]}
      </label>
      {error ? (
        <div role="alert">
          <p>{errorText(locale, error)}</p>
          <button className="button soft compact" onClick={() => setRetry((n) => n + 1)}>
            <RefreshCw size={14} />
            {text('Try again', '重试', '重試', 'Reintentar')[locale]}
          </button>
        </div>
      ) : null}
      {shops.map((shop) => (
        <article className="shop-row" key={shop.id}>
          <span className="shop-icon">
            <Coffee />
          </span>
          <div>
            <h3>{shop.name}</h3>
            <small>
              {shop.ownerName} · {shop.stock} {t('stock')}
            </small>
          </div>
          <button
            className="button soft compact"
            disabled={busy || loading || shop.stock < 1 || character.tradableCash < shop.price}
            onClick={() => onBuy(shop.id)}
          >
            C${shop.price}
          </button>
        </article>
      ))}
      {!shops.length && !error && (
        <p className="muted" role="status">
          {loading
            ? text('Finding cafés…', '正在查找小店…', '正在尋找小店…', 'Buscando cafeterías…')[locale]
            : search || friends
              ? text(
                  'No matching cafés. Try another name or filter.',
                  '没有找到匹配的小店，试试其他名字或筛选条件。',
                  '沒有找到符合的小店，試試其他名字或篩選條件。',
                  'No hay coincidencias. Prueba otro nombre o filtro.',
                )[locale]
              : t('emptyShops')}
        </p>
      )}
      <div className="directory-pages">
        <button
          className="button soft compact"
          disabled={loading || !cursors.length}
          onClick={() => setCursors((values) => values.slice(0, -1))}
        >
          {text('Previous', '上一页', '上一頁', 'Anterior')[locale]}
        </button>
        <small>{cursors.length + 1}</small>
        <button
          className="button soft compact"
          disabled={loading || !next}
          onClick={() => {
            if (next) setCursors((values) => [...values, next]);
          }}
        >
          {text('Next', '下一页', '下一頁', 'Siguiente')[locale]}
        </button>
      </div>
    </section>
  );
}
